import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar, Check, MapPin, X } from 'lucide-react';
import { mediaUrl } from '../../../utils/mediaUrl';
import * as SelfApi from '../selfApi';

const BLUE = '#47ABDB';

// The decide-on-an-invitation content, shared by the profile (inline, compact,
// inside the speaker area's invitation card - hub/SpeakerArea.js) and the Respond page
// (full-page, SpeakerInviteRespond.js), with the boxes, texts and styles it had
// before the switch. The one difference (D10): the new model keeps one
// invitation per **spot**, so each spot is answered on its own - every open spot
// gets the old assignment box followed by the old two buttons, and the old
// confirmed / thank-you / already-answered boxes per spot. The profile passes
// `assignmentId` and shows exactly one spot per card; the Respond page shows
// every open spot of the event.
const CardTitle = styled.h2`margin:0 0 .4rem;font-size:${p => (p.$compact ? '1.05rem' : '1.25rem')};color:#111D4E;`;
const Meta = styled.p`display:flex;flex-wrap:wrap;gap:.35rem 1rem;margin:0 0 1.1rem;color:#475569;font-size:.88rem;& span{display:inline-flex;align-items:center;gap:.3rem;}`;
const Cover = styled.img`display:block;width:100%;max-height:14rem;object-fit:cover;margin:0 0 1.1rem;`;
const Assignment = styled.div`border-left:3px solid ${BLUE};background:#f8fafc;padding:.65rem .85rem;margin-bottom:.55rem;font-size:.9rem;color:#1f2937;line-height:1.5;`;
const Label = styled.span`display:block;font-size:.7rem;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:#64748b;`;
const Actions = styled.div`display:flex;gap:.65rem;flex-wrap:wrap;margin-top:1.1rem;`;
const Primary = styled.button`flex:1 1 10rem;display:inline-flex;justify-content:center;align-items:center;gap:.5rem;border:0;padding:.7rem 1rem;background:${BLUE};color:#fff;font-weight:700;font-size:.92rem;cursor:pointer;transition:background-color .2s;&:hover:not(:disabled){background:#0056b3;}&:disabled{opacity:.55;cursor:not-allowed;}`;
const Secondary = styled(Primary)`background:#fff;color:#334155;border:1px solid #cbd5e1;&:hover:not(:disabled){background:#f1f5f9;}`;
const DeclinePanel = styled.div`margin-top:1.2rem;border-top:1px solid #e2e8f0;padding-top:1.1rem;`;
const Field = styled.label`display:flex;flex-direction:column;gap:.3rem;font-size:.84rem;font-weight:600;color:#334155;margin-bottom:.75rem;`;
const Input = styled.input`padding:.55rem .65rem;border:1px solid #cbd5e1;font:inherit;font-weight:400;&:focus{outline:none;border-color:${BLUE};box-shadow:0 0 0 3px rgba(71,171,219,.15);}`;
const Hint = styled.p`margin:0 0 .9rem;color:#475569;font-size:.86rem;line-height:1.5;`;
const ErrorBox = styled.div`background:#fee2e2;color:#991b1b;padding:.7rem;margin-top:.9rem;font-size:.88rem;`;
const Notice = styled.div`color:#334155;line-height:1.55;font-size:.92rem;`;
const HubLink = styled(Link)`display:inline-block;margin-top:.75rem;color:#0369a1;font-weight:600;font-size:.88rem;`;
const Success = styled.div`background:#d1fae5;color:#065f46;padding:.7rem .85rem;margin-top:.9rem;font-size:.88rem;line-height:1.5;`;
// One open spot's box and its answer, the way the old panel stacked its boxes and its buttons.
const SpotBlock = styled.div`&+&{margin-top:1.4rem;}`;

// The old panel joined a start and an end with a spaced dash; the program answers "09:00–10:30".
const spaced = (time) => (time ? time.replace('–', ' – ') : '');

// What the old assignment box showed, from a spot of GET /api/me/invitations: the session (with its
// time), and for a talk "Your talk: {title}" and its own day and time when they say more than the
// session's time. A spot on a block itself (a chair, a panelist of the session) has no talk: the
// entry is the session.
export const spotDetails = (spot, locale) => {
  const isTalk = spot.role === 'SPEAKER';
  if (!isTalk) {
    return { sessionTitle: spot.entryTitle || spot.blockTitle, sessionTime: spot.time, talkTitle: null, talkTime: null };
  }
  const day = spot.date
    ? new Date(`${spot.date}T00:00:00`).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;
  const ownTime = spot.time && spot.time !== spot.blockTime ? spot.time : null;
  return {
    sessionTitle: spot.blockTitle || null,
    sessionTime: spot.blockTitle ? spot.blockTime : null,
    talkTitle: spot.entryTitle || null,
    // Shown when it says more than the session line: a time of its own, or a talk with no session.
    talkTime: (ownTime || !spot.blockTitle) ? [day, spaced(spot.time)].filter(Boolean).join(' · ') : null,
  };
};

// `compact`: true inside the speaker area's invitation card on the profile
// (hub/SpeakerArea.js: no cover image, tighter spacing, no "go to the hub"
// link since you're already on your profile). `assignmentId`: show this one
// spot only (the speaker area's per-spot card). `onResolved`/`onEventName` let
// an embedding page react - the speaker area keeps the answer ({action,
// assignmentId, suggested}) to show on the spot's next place and has the
// profile read its lists again; SpeakerInviteRespond.js shows the event name
// in its own headline.
const InviteDecisionPanel = ({ eventId, assignmentId, compact, onResolved, onEventName }) => {
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);
  const [group, setGroup] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | missing
  // What this page just did, per spot - only ever shown while the server's own state agrees (a
  // reload, or another tab, always wins): {action: 'accepted' | 'declined', suggested} or
  // {action: 'answered'} for a spot the server says was answered elsewhere.
  const [done, setDone] = useState({});
  const [decliningId, setDecliningId] = useState(null);
  const [replacementName, setReplacementName] = useState('');
  const [replacementEmail, setReplacementEmail] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null); // {assignmentId, text}

  const findGroup = (rows) => (rows || []).find((row) => String(row.eventId) === String(eventId)) || null;

  useEffect(() => {
    let alive = true;
    setDone({});
    SelfApi.fetchInvitations()
      .then((rows) => {
        if (!alive) return;
        const found = findGroup(rows);
        if (!found) { setState('missing'); return; }
        setGroup(found);
        setState('ready');
        onEventName?.(found.event?.name, found.event?.nameEn);
      })
      .catch(() => { if (alive) setState('missing'); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const roleLabel = role => t(`speaker.engagementRole.${role}`, role ? role.charAt(0) + role.slice(1).toLowerCase() : role);
  const failure = err => (typeof err.response?.data === 'string' && err.response.data
    ? err.response.data : t('speaker.respond.err.generic', 'Something went wrong. Please try again.'));

  // The server's spot states after an answer: read again, so what is shown is what it holds. When the
  // read fails, the answer itself (the assignment's new status) is the server's word for that spot.
  const reread = async (spot, answeredStatus) => {
    try {
      const found = findGroup(await SelfApi.fetchInvitations());
      if (found) { setGroup(found); return found; }
    } catch (e) { /* keep the answer below */ }
    if (answeredStatus) {
      setGroup((current) => current && ({
        ...current,
        spots: (current.spots || []).map((s) => (s.assignmentId === spot.assignmentId
          ? { ...s, state: answeredStatus === 'CONFIRMED' ? 'CONFIRMED' : 'DECLINED' } : s)),
      }));
    }
    return null;
  };

  // A 409 is "already answered" only when the server says the spot is no longer open; any other
  // refusal (the event has started, a rule of the program) is shown with the server's own message.
  const refused = async (spot, err) => {
    if (err.response?.status === 409) {
      const found = await reread(spot, null);
      const now = (found?.spots || []).find((s) => s.assignmentId === spot.assignmentId);
      if (now && now.state !== 'OPEN') {
        setDone((current) => ({ ...current, [spot.assignmentId]: { action: 'answered' } }));
        setDecliningId(null);
        return;
      }
    }
    setError({ assignmentId: spot.assignmentId, text: failure(err) });
  };

  const accept = async spot => {
    setBusyId(spot.assignmentId); setError(null);
    try {
      const answer = await SelfApi.acceptAssignment(spot.assignmentId);
      setDone((current) => ({ ...current, [spot.assignmentId]: { action: 'accepted' } }));
      await reread(spot, answer?.status || 'CONFIRMED');
      onResolved?.({ action: 'accepted', assignmentId: spot.assignmentId, eventName: group?.event?.name, eventNameEN: group?.event?.nameEn });
    } catch (err) {
      await refused(spot, err);
    } finally { setBusyId(null); }
  };

  const decline = async (e, spot) => {
    e.preventDefault();
    setBusyId(spot.assignmentId); setError(null);
    const suggested = Boolean(replacementName.trim() || replacementEmail.trim());
    try {
      const answer = await SelfApi.declineAssignment(spot.assignmentId, {
        replacementName: replacementName.trim() || null, replacementEmail: replacementEmail.trim() || null,
      });
      setDone((current) => ({ ...current, [spot.assignmentId]: { action: 'declined', suggested } }));
      setDecliningId(null);
      await reread(spot, answer?.status || 'DECLINED');
      onResolved?.({ action: 'declined', assignmentId: spot.assignmentId, suggested, eventName: group?.event?.name, eventNameEN: group?.event?.nameEn });
    } catch (err) {
      await refused(spot, err);
    } finally { setBusyId(null); }
  };

  const hubLink = !compact && <div><HubLink to="/profile#referentenbereich">{t('speaker.respond.toHub', 'Go to the speaker area of your profile')}</HubLink></div>;

  if (state === 'loading') return <Notice>{t('speaker.respond.loading', 'Loading your invitation…')}</Notice>;
  const missing = (
    <Notice>
      {t('speaker.respond.missing', 'This invitation could not be found. It may have been withdrawn, or the event is already over.')}
      {hubLink}
    </Notice>
  );
  if (state === 'missing') return missing;

  const event = group.event || {};
  const all = (group.spots || []).filter((spot) => !assignmentId || String(spot.assignmentId) === String(assignmentId));
  // What the server says now, unless this page's own answer is the server's answer too.
  const outcomeOf = (spot) => {
    const mine = done[spot.assignmentId];
    if (mine?.action === 'accepted' && spot.state === 'CONFIRMED') return mine;
    if (mine?.action === 'declined' && spot.state === 'DECLINED') return mine;
    if (mine?.action === 'answered' && spot.state !== 'OPEN') return mine;
    return null;
  };
  // In timetable order (day, then time), as the old panel listed an event's assignments.
  const when_ = (spot) => `${spot.date || '9999'} ${spot.time || '99'}`;
  const shown = all.filter((spot) => spot.state === 'OPEN' || outcomeOf(spot))
    .sort((a, b) => when_(a).localeCompare(when_(b)));
  if (shown.length === 0) {
    // Nothing left to answer here: the old page's one notice, "already answered" when the person
    // answered, the "could not be found" one when the organizers withdrew it.
    if (all.some((spot) => spot.state === 'CONFIRMED' || spot.state === 'DECLINED')) {
      return (
        <Notice>
          {t('speaker.respond.answered', 'You have already answered this invitation – thank you.')}
          {hubLink}
        </Notice>
      );
    }
    return missing;
  }

  const when = event.dateTime
    ? new Date(event.dateTime).toLocaleDateString(intl.locale, { year: 'numeric', month: 'long', day: 'numeric' }) : null;
  const openCount = shown.filter((spot) => spot.state === 'OPEN').length;

  return (
    <>
      {!compact && event.imageUrl && <Cover src={mediaUrl(event.imageUrl)} alt="" onError={e => { e.currentTarget.style.display = 'none'; }} />}
      {!compact && (
        <>
          <CardTitle>{t('speaker.respond.title', 'You are invited to join us on stage')}</CardTitle>
          <Meta>
            {when && <span><Calendar size={15} />{when}</span>}
            {event.location && <span><MapPin size={15} />{event.location}</span>}
          </Meta>
        </>
      )}
      {shown.map((spot) => {
        const d = spotDetails(spot, intl.locale);
        const outcome = outcomeOf(spot);
        const busy = busyId === spot.assignmentId;
        return (
          <SpotBlock key={spot.invitationId || spot.assignmentId}>
            <Assignment>
              <Label>{roleLabel(spot.role)}</Label>
              {d.sessionTitle && <div><strong>{d.sessionTitle}</strong>{d.sessionTime && ` · ${spaced(d.sessionTime)}`}</div>}
              {d.talkTitle && <div>{t('speaker.respond.talk', 'Your talk: {title}', { title: d.talkTitle })}</div>}
              {d.talkTime && <div>{t('speaker.respond.talkTime', 'Time of your talk: {time}', { time: d.talkTime })}</div>}
              {spot.removed && <Hint style={{ margin: '.5rem 0 0' }}>{t('speaker.respond.spotRemoved', 'The organizers took this spot off the program.')}</Hint>}
            </Assignment>

            {outcome?.action === 'accepted' && (
              <Success>{t('speaker.respond.accepted.body', "You're confirmed - we look forward to welcoming you.")}</Success>
            )}
            {outcome?.action === 'declined' && (
              <Notice>
                <CardTitle $compact={compact} as={compact ? 'h3' : 'h2'}>{t('speaker.respond.declined.title', 'Thank you for letting us know')}</CardTitle>
                {outcome.suggested
                  ? t('speaker.respond.declined.withSuggestion', 'We have passed your answer and your suggestion on to the organizers. We are sorry you can\'t join us this time and hope to welcome you at a future event.')
                  : t('speaker.respond.declined.body', 'We have passed your answer on to the organizers. We are sorry you can\'t join us this time and hope to welcome you at a future event.')}
              </Notice>
            )}
            {outcome?.action === 'answered' && (
              <Notice>{t('speaker.respond.answered', 'You have already answered this invitation – thank you.')}</Notice>
            )}

            {!outcome && spot.state === 'OPEN' && (decliningId !== spot.assignmentId ? (
              <Actions>
                <Primary type="button" onClick={() => accept(spot)} disabled={busy}><Check size={18} />{t('speaker.respond.accept', 'Accept invitation')}</Primary>
                <Secondary type="button" disabled={busy}
                  onClick={() => { setDecliningId(spot.assignmentId); setReplacementName(''); setReplacementEmail(''); setError(null); }}>
                  <X size={18} />{t('speaker.respond.decline', 'Decline')}
                </Secondary>
              </Actions>
            ) : (
              <DeclinePanel as="form" onSubmit={e => decline(e, spot)}>
                <CardTitle as="h3" $compact style={{ fontSize: '1.02rem' }}>{t('speaker.respond.declineTitle', 'We are sorry you can\'t make it')}</CardTitle>
                <Hint>{t('speaker.respond.declineHint', 'Is there a colleague who could take your place? If you like, let us know who – both fields are optional.')}</Hint>
                <Field>
                  {t('speaker.respond.replacementName', 'Name of your suggested replacement')}
                  <Input value={replacementName} maxLength={200} autoComplete="off" onChange={e => setReplacementName(e.target.value)}
                    placeholder={t('speaker.respond.replacementNamePlaceholder', 'e.g. Dr. Jane Doe')} />
                </Field>
                <Field>
                  {t('speaker.respond.replacementEmail', 'Their email address')}
                  <Input type="email" value={replacementEmail} maxLength={200} autoComplete="off" onChange={e => setReplacementEmail(e.target.value)}
                    placeholder={t('speaker.respond.replacementEmailPlaceholder', 'name@example.com')} />
                </Field>
                <Actions style={{ marginTop: '.4rem' }}>
                  <Primary type="submit" disabled={busy} style={{ background: '#b91c1c' }}>{t('speaker.respond.confirmDecline', 'Decline invitation')}</Primary>
                  <Secondary type="button" onClick={() => { setDecliningId(null); setError(null); }} disabled={busy}>{t('speaker.respond.back', 'Back')}</Secondary>
                </Actions>
              </DeclinePanel>
            ))}
            {error && error.assignmentId === spot.assignmentId && <ErrorBox>{error.text}</ErrorBox>}
          </SpotBlock>
        );
      })}
      {openCount > 1 && (
        <Hint style={{ marginTop: '.4rem' }}>{t('speaker.respond.eachSpot', 'Answer each spot on its own – accepting one does not answer the others.')}</Hint>
      )}
      {openCount === 0 && hubLink}
    </>
  );
};

export default InviteDecisionPanel;
